import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'

import CashMachine from '@/integrations/cashmachine/cashmachine'
import { getSerial } from '@/integrations/SerialPlugin'
import { logger, rum } from '@/monitoring'
import { useConfigStore } from '@/store/config'
import { getUpdater } from '@/updater'

import { useUpdateVersion } from './plugins/updateVersion'
import type { Device as DeviceType } from './types/device'
import { isElectron } from './utils/isElectron'

class App {
  private platform = Capacitor.getPlatform()
  private updater = getUpdater()
  public isDataphone: boolean = false
  public serial: any | null = null
  private componentMounted: boolean = false
  private ipcRenderer: any

  constructor() {
    this.componentMounted = false

    try {
      this.ipcRenderer = window.require('electron').ipcRenderer
    } catch {
      // no-op
    }
    this.initDeviceSerial()
  }

  notifyAppReady() {
    this.updater.notifyAppReady()
  }

  async getDeviceInfo(): Promise<DeviceType> {
    const deviceInfo = await Device.getInfo()
    const version = await this.updater.getVersion()
    const isNew = !window.localStorage.getItem('_capuid')
    let deviceId = await Device.getId().then(res => res.identifier)
    let deviceName = deviceInfo.name ?? undefined
    if (isElectron) {
      try {
        const savedDeviceId = await this.ipcRenderer.invoke('deviceId', deviceId)
        if (isNew && savedDeviceId !== deviceId) {
          deviceId = savedDeviceId
          window.localStorage.setItem('_capuid', deviceId)
          logger.info(`Device reinstall detected`, { deviceId })
        }
        deviceName = await this.ipcRenderer.invoke('deviceName')
      } catch (e) {
        logger.error('Failed to get device id')
        rum.addError(e)
      }
    }

    return {
      id: deviceId,
      name: deviceName,
      platform: this.platform,
      model: deviceInfo.model,
      os: deviceInfo.operatingSystem,
      osVersion: deviceInfo.osVersion,
      webviewVersion: deviceInfo.webViewVersion,
      nativeVersion: version.native,
      appVersion: version.name,
      appBuild: version.build
    }
  }

  async checkForUpdates(channel?: string) {
    const device = await this.getDeviceInfo()
    this.updater.checkUpdates({ device, channel }).then(isUpdateReady => {
      if (isUpdateReady) {
        this.mountUpdateModalComponent()
      }
    })
  }

  private mountUpdateModalComponent() {
    if (this.componentMounted) return
    this.componentMounted = true
    const updateModal = useUpdateVersion()
    updateModal({
      onConfirm: () => this.confirmUpdate()
    })
  }

  async confirmUpdate() {
    if (CashMachine.isEnabled()) {
      try {
        await CashMachine.close()
      } catch {
        logger.warn('Failed to close cash machine')
      }
    }
    await this.updater.installUpdate()
  }

  async initDeviceSerial() {
    try {
      const info = await Device.getInfo()
      if (info.model === 'Saturn1000F2') {
        this.isDataphone = true
        this.serial = await getSerial()
      }
    } catch {
      this.serial = null
    }
  }

  minimize() {
    this.ipcRenderer.invoke('window:minimize')
  }

  quit() {
    this.ipcRenderer.invoke('window:close')
  }

  async getScreenInfo() {
    if (isElectron && this.ipcRenderer) {
      try {
        return await this.ipcRenderer.invoke('screenInfo')
      } catch {
        return { totalScreens: 1, secondScreenInfo: null }
      }
    }
    return { totalScreens: 1, secondScreenInfo: null }
  }

  playSound(name: 'payment' | 'cancel' | 'notification' | 'alert') {
    const config = useConfigStore()
    if (!config.mute) {
      const sound = new Audio(`${name}.mp3`)
      sound.play()
    }
  }
}

export default new App()
